import React, { useState } from 'react'
import { ChevronDown, ChevronRight, FileDown } from 'lucide-react'
import { Button } from '../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { CryptoIcon, TabsHeader, TabsHeaderActions, TabsHeaderTitle } from '../ui'
import { SkeletonVolumes } from '../skeleton/Skeleton'

export type VolumesTabVariant = 'default' | 'admin' | 'valiant'

export interface MintLpFeeData {
  lpId: string
  lpName: string
  fee: string
}

export interface LpFeeTotalData {
  lpId: string
  lpName: string
  totalUsd: string
  totalUsdRaw: number
  groupFeeUsd?: string
}

export interface MintData {
  mint: string
  symbol: string
  volume: string
  groupFee?: string
  lpFee?: string
  lpFees?: MintLpFeeData[]
  valiantFee?: string
}

export interface MonthlyVolumeData {
  month: string
  totalUsd: string
  totalUsdRaw: number
  totalGroupFeeUsd?: string
  totalGroupFeeUsdRaw?: number
  totalLpFeeUsd?: string
  totalLpFeeUsdRaw?: number
  lpFees?: LpFeeTotalData[]
  totalValiantFeeUsd?: string
  totalValiantFeeUsdRaw?: number
  mints: MintData[]
}

export interface VolumesTabViewProps {
  monthlyVolumes: MonthlyVolumeData[]
  loading?: boolean
  onExportCSV?: () => void
  showExportButton?: boolean
  searchTerm?: string
  variant?: VolumesTabVariant
  showGroupFeesPerLp?: boolean
}

function formatFeeDisplay(value: string): string {
  if (value === '0' || value === '0.00') return '+0'
  return value.startsWith('+') ? value : `+${value}`
}

function FeeSummary({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <span className="text-sm font-medium text-green-600">
        {formatFeeDisplay(value)} <span className="text-gray-500 font-normal">USD</span>
      </span>
    </div>
  )
}

function VolumeSummary({ label = 'Volumes', value }: { label?: string; value: string }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 bg-gray-400 rounded-full" />
        <span className="text-sm font-semibold">{value}</span>
        <span className="text-sm text-gray-500">USD</span>
      </div>
    </div>
  )
}

function MintFeeValue({ value, symbol }: { value: string; symbol: string }) {
  if (value === '0' || value === '0.00') {
    return <span className="text-sm text-gray-400">0 {symbol}</span>
  }

  return (
    <span className="text-sm font-medium text-green-600">
      {formatFeeDisplay(value)} <span className="text-gray-500 font-normal">{symbol}</span>
    </span>
  )
}

function MintVolumeValue({ value, symbol }: { value: string; symbol: string }) {
  return (
    <span className="text-sm font-semibold text-gray-900">
      {value} <span className="text-gray-500 font-normal">{symbol}</span>
    </span>
  )
}

function collectLpColumns(monthlyVolumes: MonthlyVolumeData[]): Array<{ lpId: string; lpName: string }> {
  const byId = new Map<string, string>()
  for (const volume of monthlyVolumes) {
    for (const lpFee of volume.lpFees ?? []) {
      byId.set(lpFee.lpId, lpFee.lpName)
    }
    for (const mint of volume.mints) {
      for (const lpFee of mint.lpFees ?? []) {
        byId.set(lpFee.lpId, lpFee.lpName)
      }
    }
  }
  return Array.from(byId.entries())
    .map(([lpId, lpName]) => ({ lpId, lpName }))
    .sort((a, b) => a.lpName.localeCompare(b.lpName))
}

export function VolumesTabView({
  monthlyVolumes,
  loading = false,
  onExportCSV,
  showExportButton = false,
  searchTerm = '',
  variant = 'default',
  showGroupFeesPerLp = false,
}: VolumesTabViewProps) {
  const [expandedRows, setExpandedRows] = useState(new Set<string>())

  const showValiantFees = variant === 'valiant'
  const showGroupFees = variant === 'valiant' || variant === 'admin'
  const showLpFees = variant === 'valiant' || variant === 'admin'
  const showVolumes = true

  const lpColumns = showLpFees ? collectLpColumns(monthlyVolumes) : []

  const handleExportCSV = () => {
    if (monthlyVolumes.length === 0) {
      alert('No data available to export')
      return
    }

    try {
      const allMints = new Map<string, { symbol: string; name: string }>()
      monthlyVolumes.forEach((volume) => {
        if (volume.mints && Array.isArray(volume.mints)) {
          volume.mints.forEach((mint) => {
            if (mint && mint.mint && mint.symbol && !allMints.has(mint.mint)) {
              allMints.set(mint.mint, {
                symbol: mint.symbol,
                name: mint.mint.replace(/-/g, ' '),
              })
            }
          })
        }
      })
      const mintsArray = Array.from(allMints.entries()).sort(([a], [b]) => a.localeCompare(b))
      const exportLpColumns = collectLpColumns(monthlyVolumes)

      const headers = ['Month']
      if (showValiantFees) {
        headers.push(
          ...mintsArray.map(([, { symbol, name }]) => `Valiant fee ${symbol} (${name})`),
          'Total Valiant Fee USD',
        )
      }
      if (showGroupFees) {
        headers.push(
          ...mintsArray.map(([, { symbol, name }]) => `Group fee ${symbol} (${name})`),
          'Total Group Fee USD',
        )
      }
      if (showLpFees) {
        for (const lp of exportLpColumns) {
          headers.push(
            ...mintsArray.map(
              ([, { symbol, name }]) => `${lp.lpName} volume ${symbol} (${name})`,
            ),
            `Total ${lp.lpName} Volume USD`,
          )
        }
      }
      if (showVolumes) {
        headers.push(
          ...mintsArray.map(([, { symbol, name }]) => `${symbol} (${name})`),
          'Total USD Value',
        )
      }

      const csvData = monthlyVolumes.map((volume) => {
        const row: string[] = [volume.month || '']

        if (showValiantFees) {
          mintsArray.forEach(([mintKey]) => {
            const mint = volume.mints?.find((m) => m?.mint === mintKey)
            row.push(mint?.valiantFee ?? '0')
          })
          row.push((volume.totalValiantFeeUsdRaw ?? 0).toFixed(2))
        }

        if (showGroupFees) {
          mintsArray.forEach(([mintKey]) => {
            const mint = volume.mints?.find((m) => m?.mint === mintKey)
            row.push(mint?.groupFee ?? '0')
          })
          row.push(volume.totalGroupFeeUsd ?? '0.00')
        }

        if (showLpFees) {
          for (const lp of exportLpColumns) {
            mintsArray.forEach(([mintKey]) => {
              const mint = volume.mints?.find((m) => m?.mint === mintKey)
              const mintLp = mint?.lpFees?.find((entry) => entry.lpId === lp.lpId)
              row.push(mintLp?.fee ?? '0')
            })
            const monthLp = volume.lpFees?.find((entry) => entry.lpId === lp.lpId)
            row.push((monthLp?.totalUsdRaw ?? 0).toFixed(2))
          }
        }

        if (showVolumes) {
          mintsArray.forEach(([mintKey]) => {
            const mint = volume.mints?.find((m) => m?.mint === mintKey)
            row.push(mint?.volume ?? '0')
          })
          row.push(volume.totalUsdRaw.toFixed(2))
        }

        return row
      })

      const csvContent = [headers, ...csvData]
        .map((row) =>
          row
            .map((cell) => {
              const cellStr = String(cell || '')
              const needsQuotes =
                cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')
              if (needsQuotes) {
                const escaped = cellStr.replace(/"/g, '""')
                return `"${escaped}"`
              }
              return cellStr
            })
            .join(','),
        )
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `monthly-volumes-${new Date().toISOString().split('T')[0]}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting CSV:', error)
      alert('Error exporting CSV. Please try again.')
    }
  }

  const exportCSV = onExportCSV || handleExportCSV

  const filteredData = monthlyVolumes.filter((volume) =>
    volume.month.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const toggleRow = (month: string) => {
    const newExpandedRows = new Set(expandedRows)
    if (newExpandedRows.has(month)) {
      newExpandedRows.delete(month)
    } else {
      newExpandedRows.add(month)
    }
    setExpandedRows(newExpandedRows)
  }

  const renderMonthSummaries = (volume: MonthlyVolumeData) => (
    <div className="flex items-center justify-end gap-8">
      {showValiantFees && (
        <FeeSummary label="Valiant fees" value={volume.totalValiantFeeUsd ?? '0'} />
      )}
      {showGroupFees && <FeeSummary label="Group fees" value={volume.totalGroupFeeUsd ?? '0'} />}
      {showLpFees &&
        lpColumns.map((lp) => {
          const lpVolume = volume.lpFees?.find((entry) => entry.lpId === lp.lpId)
          return (
            <div key={lp.lpId} className="flex flex-col items-end gap-2">
              <VolumeSummary label={`${lp.lpName} volumes`} value={lpVolume?.totalUsd ?? '0'} />
              {showGroupFeesPerLp && (
                <FeeSummary
                  label={`${lp.lpName} group fees`}
                  value={lpVolume?.groupFeeUsd ?? '0'}
                />
              )}
            </div>
          )
        })}
      {showVolumes && <VolumeSummary value={volume.totalUsd} />}
    </div>
  )

  const renderMintValues = (mint: MintData) => (
    <div className="flex items-center justify-end gap-8">
      {showValiantFees && (
        <div className="min-w-[120px] text-right">
          <MintFeeValue value={mint.valiantFee ?? '0'} symbol={mint.symbol} />
        </div>
      )}
      {showGroupFees && (
        <div className="min-w-[120px] text-right">
          <MintFeeValue value={mint.groupFee ?? '0'} symbol={mint.symbol} />
        </div>
      )}
      {showLpFees &&
        lpColumns.map((lp) => {
          const mintLp = mint.lpFees?.find((entry) => entry.lpId === lp.lpId)
          return (
            <div key={lp.lpId} className="min-w-[120px] text-right">
              <MintVolumeValue value={mintLp?.fee ?? '0'} symbol={mint.symbol} />
            </div>
          )
        })}
      {showVolumes && (
        <div className="min-w-[120px] text-right">
          <MintVolumeValue value={mint.volume} symbol={mint.symbol} />
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      <TabsHeader className="flex-row justify-between">
        <TabsHeaderTitle>Monthly volumes</TabsHeaderTitle>

        <TabsHeaderActions>
          {showExportButton && (
            <Button onClick={exportCSV} variant="main_action">
              <FileDown className="w-4 h-4" />
              Export CSV
            </Button>
          )}
        </TabsHeaderActions>
      </TabsHeader>

      {loading ? (
        <SkeletonVolumes />
      ) : (
        <>
          <div className="rounded-md">
            <Table>
              <TableHeader>
                <TableRow className="h-[60px]">
                  <TableHead>Month (First to Last day of each month)</TableHead>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-8 pr-0">
                      {showValiantFees && (
                        <span className="min-w-[120px] text-[10px] font-medium uppercase tracking-wide text-gray-500">
                          Valiant fees
                        </span>
                      )}
                      {showGroupFees && (
                        <span className="min-w-[120px] text-[10px] font-medium uppercase tracking-wide text-gray-500">
                          Group fees
                        </span>
                      )}
                      {showLpFees &&
                        lpColumns.map((lp) => (
                          <span
                            key={lp.lpId}
                            className="min-w-[120px] text-[10px] font-medium uppercase tracking-wide text-gray-500"
                          >
                            {showGroupFeesPerLp ? lp.lpName : `${lp.lpName} volumes`}
                          </span>
                        ))}
                      {showVolumes && (
                        <span className="min-w-[120px] text-[10px] font-medium uppercase tracking-wide text-gray-500">
                          Volumes
                        </span>
                      )}
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {filteredData.map((volume) => (
                  <React.Fragment key={volume.month}>
                    <TableRow
                      className="hover:bg-gray-50 cursor-pointer h-[60px]"
                      onClick={() => toggleRow(volume.month)}
                    >
                      <TableCell className="font-medium flex items-center h-[60px]">
                        {expandedRows.has(volume.month) ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        {volume.month}
                      </TableCell>
                      <TableCell className="text-right">{renderMonthSummaries(volume)}</TableCell>
                    </TableRow>

                    {expandedRows.has(volume.month) && (
                      <TableRow className="h-auto">
                        <TableCell colSpan={2} className="pb-4 pt-0">
                          <div className="ml-8 pt-2 border-gray-200">
                            <div className="space-y-2">
                              {volume.mints.length ? (
                                volume.mints.map((mint) => (
                                  <div
                                    key={mint.mint}
                                    className="flex items-center justify-between py-2 px-3 hover:bg-gray-100 rounded"
                                  >
                                    <div className="flex items-center gap-3">
                                      <CryptoIcon symbol={mint.symbol} />
                                      <span className="text-sm font-medium text-gray-700">
                                        {mint.symbol}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {mint.mint.replace(/-/g, ' ')}
                                      </span>
                                    </div>
                                    {renderMintValues(mint)}
                                  </div>
                                ))
                              ) : (
                                <div className="flex items-center justify-center h-full py-4">
                                  <span className="text-gray-500">No Monthly Volume</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredData.length === 0 && searchTerm && (
            <div className="text-center py-8 text-gray-500">
              No results found for "{searchTerm}"
            </div>
          )}
        </>
      )}
    </div>
  )
}
